import { useCallback, useEffect, useMemo, useState } from 'react'

import { useClaimChecklist } from '@/admin/hooks/use-lab-tasks'
import { ChecklistRequestFields } from '@/admin/components/claims/checklist-request-fields'
import {
  parseDocumentRequest,
  type DocumentRequestValue,
} from '@/admin/components/claims/document-request-value'

/**
 * WPS-130 - the "Request More Documents" capture-field widget.
 *
 * A thin adapter around the shared {@link ChecklistRequestFields} picker: it
 * bridges the workflow capture-field contract (a single JSON string value,
 * selected by ``configuration_data.widget === "document_request"``) to the
 * picker's controlled props, and owns the intake-side pre-tick policy (chase
 * everything still outstanding). All rendering - the checklist checkboxes, the
 * "something else" rows, the note - lives in the shared picker, which the Lab
 * "Request Additional Photos" dialog renders too, so the two surfaces cannot
 * drift.
 *
 * Selected by config, never by action key or tenant, so any tenant can opt an
 * action into it.
 */
export function DocumentRequestInput({
  claimUuid,
  value,
  onChange,
}: {
  claimUuid: string
  value: unknown
  onChange: (next: string) => void
}) {
  const { data: checklist, isPending, isError } = useClaimChecklist(claimUuid)
  const current = useMemo(() => parseDocumentRequest(value), [value])

  const [seeded, setSeeded] = useState(false)

  const emit = useCallback(
    (next: DocumentRequestValue) => onChange(JSON.stringify(next)),
    [onChange],
  )

  const documents = useMemo(
    () => (checklist?.items ?? []).filter((i) => i.kind === 'document'),
    [checklist],
  )
  const photos = useMemo(() => checklist?.all_photo_categories ?? [], [checklist])

  // Pre-tick what has not arrived yet: the common case is "chase everything
  // still outstanding", and the assessor can untick from there.
  useEffect(() => {
    if (!checklist || seeded) return
    setSeeded(true)
    if (current.requirement_keys.length > 0) return
    const outstanding = [
      ...documents.filter((d) => !d.satisfied).map((d) => d.key),
      ...photos.filter((p) => !p.satisfied).map((p) => p.key),
    ]
    if (outstanding.length) {
      emit({ ...current, requirement_keys: outstanding })
    }
  }, [checklist, seeded, documents, photos, current, emit])

  const toggle = (key: string) => {
    const has = current.requirement_keys.includes(key)
    emit({
      ...current,
      requirement_keys: has
        ? current.requirement_keys.filter((k) => k !== key)
        : [...current.requirement_keys, key],
    })
  }

  const selectedKeys = useMemo(
    () => new Set(current.requirement_keys),
    [current.requirement_keys],
  )

  return (
    <ChecklistRequestFields
      documents={documents}
      photos={photos}
      isPending={isPending}
      isError={isError}
      selectedKeys={selectedKeys}
      onToggleKey={toggle}
      customItems={current.custom_items}
      onCustomItemsChange={(items) => emit({ ...current, custom_items: items })}
      note={current.note}
      onNoteChange={(note) => emit({ ...current, note })}
      warnWhenEmpty
    />
  )
}
